import { useEffect, useState } from 'react'

export type ThemeId =
  | 'phosphor'
  | 'monokai'
  | 'kanagawa'
  | 'zenburn'
  | 'ayu'
  | 'opencode'
  | 'solarized'
  | 'gruvbox-light'
  | 'bluloco-light'
  | 'zenburn-light'
  | 'ayu-light'
  | 'opencode-light'

export type ThemeMode = 'dark' | 'light'

export interface ThemeMeta {
  id: ThemeId
  label: string
  mode: ThemeMode
}

export const THEMES: ThemeMeta[] = [
  { id: 'phosphor', label: 'phosphor', mode: 'dark' },
  { id: 'monokai', label: 'monokai', mode: 'dark' },
  { id: 'kanagawa', label: 'kanagawa', mode: 'dark' },
  { id: 'zenburn', label: 'zenburn', mode: 'dark' },
  { id: 'ayu', label: 'ayu', mode: 'dark' },
  { id: 'opencode', label: 'opencode', mode: 'dark' },
  { id: 'solarized', label: 'solarized', mode: 'light' },
  { id: 'gruvbox-light', label: 'gruvbox light', mode: 'light' },
  { id: 'bluloco-light', label: 'bluloco light', mode: 'light' },
  { id: 'zenburn-light', label: 'zenburn light', mode: 'light' },
  { id: 'ayu-light', label: 'ayu light', mode: 'light' },
  { id: 'opencode-light', label: 'opencode light', mode: 'light' },
]

const DEFAULT_THEME: ThemeId = 'phosphor'
const THEME_KEY = 'remote-ai.theme'
const VALID_IDS = new Set<string>(THEMES.map((t) => t.id))

function modeOf(id: ThemeId): ThemeMode {
  return THEMES.find((t) => t.id === id)?.mode ?? 'dark'
}

export function getTheme(): ThemeId {
  try {
    const raw = localStorage.getItem(THEME_KEY)
    if (!raw) return DEFAULT_THEME
    if (raw === 'dark') return 'phosphor'
    if (raw === 'light') return 'solarized'
    return VALID_IDS.has(raw) ? (raw as ThemeId) : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

export function applyTheme(id: ThemeId): void {
  const root = document.documentElement
  const mode = modeOf(id)
  const stale = Array.from(root.classList).filter(
    (c) => c.startsWith('theme-') || c === 'mode-dark' || c === 'mode-light' || c === 'light',
  )
  stale.forEach((c) => root.classList.remove(c))
  root.classList.add(`theme-${id}`)
  root.classList.add(`mode-${mode}`)
  try {
    localStorage.setItem(THEME_KEY, id)
  } catch {
    /* storage may be disabled; theme still applies for the session */
  }
}

export function useTheme(): [ThemeId, (t: ThemeId) => void] {
  const [theme, setThemeState] = useState<ThemeId>(() => getTheme())

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  return [theme, setThemeState]
}
